import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const field =
  "flex-1 px-4 py-3 rounded-xl bg-black/40 border border-amber-500/20 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/60";

export const MembersManager = () => {
  const [list, setList] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => api.get("/admin/members").then(({ data }) => setList(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/admin/members", { email, name });
      toast.success("Membre ajouté");
      setEmail(""); setName("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Retirer ce membre ?")) return;
    await api.delete(`/admin/members/${id}`);
    toast.success("Membre retiré");
    load();
  };

  return (
    <div data-testid="admin-members-manager">
      <h1 className="font-serif text-3xl text-amber-50 mb-2">Membres Le Cercle</h1>
      <p className="text-slate-400 mb-8">La communauté d'éveil ({list.length} membres).</p>

      <form onSubmit={add} className="glass rounded-2xl p-5 mb-8 flex flex-col sm:flex-row gap-3">
        <input className={field} placeholder="Prénom (optionnel)" value={name} onChange={(e) => setName(e.target.value)} data-testid="member-name-input" />
        <input required type="email" className={field} placeholder="Email du membre" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="member-email-input" />
        <button type="submit" disabled={adding} data-testid="member-add-button" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-amber-300 to-yellow-600 text-[#08080A] font-medium hover:scale-[1.02] transition-transform disabled:opacity-60">
          {adding ? <Loader2 size={18} className="animate-spin" /> : <><Plus size={18} /> Ajouter</>}
        </button>
      </form>

      <div className="glass rounded-2xl divide-y divide-amber-500/10">
        {list.length === 0 ? (
          <p className="p-6 text-slate-500">Aucun membre pour le moment.</p>
        ) : (
          list.map((m) => (
            <div key={m.id} className="flex items-center justify-between p-4 sm:p-5" data-testid={`member-row-${m.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-300 text-sm font-mono-accent">
                  {(m.name || m.email)[0].toUpperCase()}
                </div>
                <div>
                  {m.name && <div className="text-amber-100">{m.name}</div>}
                  <div className="text-sm text-slate-400 inline-flex items-center gap-1"><Mail size={12} /> {m.email}</div>
                </div>
              </div>
              <button onClick={() => remove(m.id)} data-testid={`member-delete-${m.id}`} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400"><Trash2 size={16} /></button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
